/**
 * MultiProjectSelector — modal to pick (project, sync) pairs for multi-Gantt mode.
 * Returns selection: [{ project_id, sync_id, color }]
 */
import { useEffect, useState } from "react";
import { listProjects, listSyncs } from "../lib/api";
import { X, Plus, Trash2 } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const COLORS = ["#F97316", "#2563EB", "#16A34A", "#DC2626", "#A855F7", "#EAB308", "#0EA5E9", "#EC4899"];

const newRowUid = () => `row_${Math.random().toString(36).slice(2, 10)}`;

export default function MultiProjectSelector({ open, onClose, value = [], onChange }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [syncsByProject, setSyncsByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [local, setLocal] = useState(() => value.map((r) => ({ _uid: newRowUid(), ...r })));

  useEffect(() => { setLocal(value.map((r) => ({ _uid: r._uid || newRowUid(), ...r }))); }, [value]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const d = await listProjects();
        setProjects(d.projects || []);
      } finally { setLoading(false); }
    })();
  }, [open]);

  const loadSyncs = async (pid) => {
    if (syncsByProject[pid]) return;
    const d = await listSyncs(pid);
    setSyncsByProject((p) => ({ ...p, [pid]: d.syncs || [] }));
  };

  const addRow = () => {
    setLocal([...local, { _uid: newRowUid(), project_id: "", sync_id: "", color: COLORS[local.length % COLORS.length] }]);
  };
  const removeRow = (i) => setLocal(local.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => {
    const next = [...local];
    next[i] = { ...next[i], ...patch };
    setLocal(next);
  };

  const apply = () => {
    // Strip internal _uid before persisting upstream
    const sanitized = local.filter((r) => r.project_id && r.sync_id)
      .map(({ _uid, ...rest }) => rest);
    onChange(sanitized);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid="multi-project-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold">{t("multi_title")}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto p-4 flex-1 scrollbar-thin">
          {loading ? (
            <div className="text-sm text-zinc-500">{t("loading")}</div>
          ) : (
            <div className="space-y-2">
              {local.map((row, i) => (
                <div key={row._uid} className="grid grid-cols-12 gap-2 items-center" data-testid={`multi-row-${i}`}>
                  <input type="color" value={row.color || COLORS[i % COLORS.length]} onChange={(e) => setRow(i, { color: e.target.value })} className="col-span-1 h-9 border border-zinc-200 rounded" />
                  <select value={row.project_id} onChange={(e) => { setRow(i, { project_id: e.target.value, sync_id: "" }); loadSyncs(e.target.value); }} className="col-span-5 px-2 py-1.5 border border-zinc-200 rounded-md text-xs" data-testid={`multi-proj-${i}`}>
                    <option value="">{t("multi_project_placeholder")}</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select value={row.sync_id} onChange={(e) => setRow(i, { sync_id: e.target.value })} disabled={!row.project_id || !syncsByProject[row.project_id]} className="col-span-5 px-2 py-1.5 border border-zinc-200 rounded-md text-xs font-mono disabled:bg-zinc-100" data-testid={`multi-sync-${i}`}>
                    <option value="">{t("multi_version_placeholder")}</option>
                    {(syncsByProject[row.project_id] || []).map((s) => (
                      <option key={s.id} value={s.id}>v{s.version} · {s.name}{s.is_baseline ? " ★" : ""}</option>
                    ))}
                  </select>
                  <button onClick={() => removeRow(i)} className="col-span-1 p-1.5 text-zinc-400 hover:text-red-600 hover:bg-zinc-100 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button onClick={addRow} className="inline-flex items-center gap-1.5 text-xs font-semibold text-cs-accent hover:underline mt-2" data-testid="multi-add-row">
                <Plus size={12} /> {t("multi_add_project")}
              </button>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="multi-cancel">{t("multi_cancel")}</button>
          <button onClick={apply} className="px-4 py-1.5 text-sm font-semibold bg-cs-accent text-white rounded-md hover:bg-cs-accentHover" data-testid="multi-apply">{t("multi_apply")}</button>
        </div>
      </div>
    </div>
  );
}
